(() => {
  if (window.rtLazyVideoInitialized) return;
  window.rtLazyVideoInitialized = true;

  const selector = '[data-rt-lazy-autoplay-video]';
  const containers = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');

  const updateToggle = container => {
    const state = containers.get(container);
    if (!state?.toggle) return;
    const paused = !state.video || state.video.paused;
    state.toggle.textContent = paused ? state.toggle.dataset.playLabel : state.toggle.dataset.pauseLabel;
    state.toggle.setAttribute('aria-label', state.toggle.textContent);
    if (container.hasAttribute('data-native-controls')) state.toggle.hidden = !state.playbackBlocked;
  };

  const showFallback = container => {
    const state = containers.get(container);
    if (!state) return;
    const poster = container.querySelector('.rt-lazy-autoplay-video__poster');
    if (poster) poster.hidden = false;
    state.playbackBlocked = true;
    container.setAttribute('data-playback-blocked', '');
    updateToggle(container);
  };

  const play = container => {
    const state = containers.get(container);
    if (!state?.video) return;
    const request = state.video.play();
    if (request?.catch) request.catch(() => showFallback(container));
  };

  const sync = container => {
    const state = containers.get(container);
    if (!state?.video) return;
    if (!state.visible || document.hidden || motion.matches && !state.motionOverride) {
      state.policyPaused = true;
      state.video.pause();
      if (state.visible && !document.hidden && motion.matches && !state.motionOverride) showFallback(container);
    } else if (!state.userPaused) {
      play(container);
    }
    updateToggle(container);
  };

  const load = container => {
    const state = containers.get(container);
    if (!state || state.video) return;
    const template = container.querySelector('template[data-rt-video-template]');
    const source = template?.content.querySelector('video');
    if (!source) return;

    const video = state.video = source.cloneNode(true);
    video.autoplay = false;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.preload = 'metadata';
    video.setAttribute('aria-label', container.dataset.videoTitle || 'Video');

    const poster = container.querySelector('.rt-lazy-autoplay-video__poster');
    const options = { signal: state.controller.signal };
    video.addEventListener('loadeddata', () => sync(container), options);
    video.addEventListener('error', () => showFallback(container), options);
    video.addEventListener('pause', () => {
      if (!state.policyPaused && state.visible && !document.hidden) state.userPaused = true;
      updateToggle(container);
    }, options);
    video.addEventListener('play', () => {
      state.policyPaused = false;
      state.userPaused = false;
      if (motion.matches) state.motionOverride = true;
      updateToggle(container);
    }, options);
    video.addEventListener('playing', () => {
      if (poster) poster.hidden = true;
      state.playbackBlocked = false;
      container.removeAttribute('data-playback-blocked');
      updateToggle(container);
    }, options);

    template.before(video);
    sync(container);
  };

  const near = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) {
      load(entry.target);
      near.unobserve(entry.target);
    }
  }), { rootMargin: '300px 0px' });

  const visible = new IntersectionObserver(entries => entries.forEach(entry => {
    const state = containers.get(entry.target);
    if (!state) return;
    state.visible = entry.isIntersecting;
    if (state.visible) load(entry.target);
    sync(entry.target);
  }), { threshold: 0.01 });

  const find = root => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const initialize = root => find(root).forEach(container => {
    if (containers.has(container)) return;
    const state = {
      controller: new AbortController(),
      visible: false,
      userPaused: false,
      motionOverride: false,
      playbackBlocked: false,
      toggle: container.querySelector('[data-video-toggle]')
    };
    containers.set(container, state);
    state.toggle?.addEventListener('click', () => {
      load(container);
      if (!state.video) return;
      if (state.video.paused) {
        state.userPaused = false;
        state.motionOverride = true;
        play(container);
      } else {
        state.userPaused = true;
        state.video.pause();
      }
      updateToggle(container);
    }, { signal: state.controller.signal });
    near.observe(container);
    visible.observe(container);
    updateToggle(container);
  });

  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => find(event.target).forEach(container => {
    const state = containers.get(container);
    state?.controller.abort();
    near.unobserve(container);
    visible.unobserve(container);
    if (state?.video) {
      state.video.pause();
      state.video.removeAttribute('src');
      state.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
      state.video.load();
      state.video.remove();
    }
    containers.delete(container);
  }));
  document.addEventListener('visibilitychange', () => containers.forEach((state, container) => sync(container)));
  motion.addEventListener('change', () => containers.forEach((state, container) => {
    state.motionOverride = false;
    sync(container);
  }));
})();
